// @ts-strict-ignore
import { isEmpty } from 'lodash-es';
import { BoardTimeline } from '../../model/board-timeline';
import { ClientNode } from '../../model/client-model/client-node';
import { NodeListItem } from '../../model/node-list-item';
import { removeFloatingPointPrecisionError } from '../remove-floating-point-precision-error';

type Node = ClientNode | NodeListItem;

type NodeEmissionParams = {
  allNodes: Node[];
  node: Node;
  timeline?: BoardTimeline;
  isV4?: boolean;
};

export function childrenHaveEmissions({ allNodes, node, timeline, isV4 }: NodeEmissionParams): boolean {
  const childNodes = _getAllChildNodes(allNodes, node);
  return childNodes.some((childNode) => _hasEmissions({ node: childNode, timeline, isV4 }));
}

export function parentHaveEmissions({ allNodes, node, timeline, isV4 }: NodeEmissionParams): boolean {
  const parentNodes = _getAllParentNodes(allNodes, node);
  return parentNodes.some((parentNode) => _hasRelevantEmissions({ allNodes, node: parentNode, timeline, isV4 }));
}

export function getSummarizedEmissions({ allNodes, node, timeline, isV4 }: NodeEmissionParams): {
  [year: string]: number;
} {
  const result: { [year: string]: number } = {};
  const childNodes = _getAllChildNodes(allNodes, node);

  childNodes.forEach((childNode) => {
    if (!childrenHaveEmissions({ allNodes, node: childNode, timeline, isV4 })) {
      const data = _parseHistoricalEmissionsData({
        data: childNode.nodeProperties.historicalEmissionData,
        timeline,
        isV4,
      });
      if (!isEmpty(data)) {
        Object.keys(data).forEach((key) => {
          const year = Number(key);
          const outcome = data[year];

          if (result[year]) {
            result[year] = removeFloatingPointPrecisionError(result[year] + outcome);
          } else {
            result[year] = outcome;
          }
        });
      }
    }
  });

  return result;
}

export function showNodeEmissionMarker({ allNodes, node, timeline, isV4 }: NodeEmissionParams): boolean {
  // The rules for when to show a marker are:
  // 1. If current node does not have anything to show in table, but one of its sibling does, a marker should be visible.
  // 2. If a child (at any level) has a marker, a marker should be visible in current node.

  // Check rule 1
  if (!_validateEmissions({ allNodes, node, timeline, isV4 })) {
    return true;
  }

  // Check rule 2
  const childNodes = _getAllChildNodes(allNodes, node);
  if (childNodes.some((childNode) => !_validateEmissions({ allNodes, node: childNode, timeline, isV4 }))) {
    return true;
  }

  return false;
}

//====== PRIVATE HELP METHODS ======\\

function _getAllChildNodes(allNodes: Node[], node: Node): Node[] {
  const result = [];
  const search = (_node: Node) => {
    _getChildNodes(allNodes, _node).forEach((childNode) => {
      result.push(childNode);
      search(childNode);
    });
  };
  search(node);
  return result;
}

function _getAllParentNodes(allNodes: Node[], node: Node): Node[] {
  const result = [];
  const search = (_node: Node) => {
    const parentNode = _getParentNode(allNodes, _node);
    if (parentNode) {
      result.push(parentNode);
      search(parentNode);
    }
  };
  search(node);
  return result;
}

function _getChildNodes(allNodes: Node[], parentNode: Node): Node[] {
  return allNodes.filter((node) => parentNode.childNodes.includes(node.id));
}

function _getParentNode(allNodes: Node[], node: Node): Node | undefined {
  return allNodes.find((n) => n.childNodes.includes(node.id));
}

function _getSiblingNodes(allNodes: Node[], node: Node): Node[] {
  const parent = allNodes.find((n) => n.childNodes.includes(node.id));
  return allNodes.filter((n) => n.id !== node.id && parent?.childNodes.includes(n.id));
}

function _hasEmissions({ node, timeline, isV4 }: { node: Node; timeline?: BoardTimeline; isV4?: boolean }): boolean {
  return !isEmpty(
    _parseHistoricalEmissionsData({ data: node?.nodeProperties?.historicalEmissionData, timeline, isV4 })
  );
}

function _hasRelevantEmissions({ allNodes, node, timeline, isV4 }: NodeEmissionParams): boolean {
  if (childrenHaveEmissions({ allNodes, node, timeline, isV4 })) {
    return false;
  }
  return _hasEmissions({ node, timeline, isV4 });
}

// Returns an emission data object, only including defined values and values for years within timeline
function _parseHistoricalEmissionsData({
  data = {},
  timeline,
  isV4,
}: {
  data: { [year: number]: number };
  timeline?: BoardTimeline;
  isV4?: boolean;
}): { [year: number]: number } {
  return Object.entries(data).reduce((result: { [year: number]: number }, [key, outcome]) => {
    const year = Number(key);
    if (outcome === undefined) {
      return result;
    }
    if (timeline) {
      if (
        year >= timeline.boardStartYear &&
        year <= (isV4 ? timeline.activityBasedInventoryStartYear : timeline.scenarioStartYear)
      ) {
        result[year] = outcome;
      }
    } else {
      result[year] = outcome;
    }
    return result;
  }, {});
}

// "Valid" here means a node will have something to show in table.
function _validateEmissions({ allNodes, node, timeline, isV4 }: NodeEmissionParams): boolean {
  const isValid = (_node: Node) =>
    _hasEmissions({ node: _node, timeline, isV4 }) || childrenHaveEmissions({ allNodes, node: _node, timeline, isV4 });

  if (isValid(node)) {
    return true;
  }

  const siblings = _getSiblingNodes(allNodes, node);
  return !siblings.some(isValid);
}
